import { IconClose } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { DropZone } from "@/components/elements/DropZone/DropZone";
import { FileItem } from "@/components/elements/FileItem/FileItem";
import { FormField } from "@/components/elements/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { EXTENSIONS, MIME_TYPES } from "@/constants/file";
import { TMatchForm } from "@/features/match/types/MatchForm";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { useDialog } from "@/hooks/useDialog";
import { black, divider, text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { TExtendFile } from "@/types/file";
import { makeUuid } from "@/utils/pieces";
import { Box, Collapse, Grid, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

type TMatchBasicInfoFormProps = {
  defaultValues: Partial<TMatchForm>;
};

export const MatchBasicInfoForm: FCC<TMatchBasicInfoFormProps> = ({ defaultValues }) => {
  const methods = useFormContext<TMatchForm>();
  const { control, setValue } = methods;
  const { expanded, toggle } = useCollapsible();

  // Build initial display file from existing filePath (already saved on server)
  const initialLicenseFile = useMemo<TExtendFile[]>(() => {
    if (!defaultValues?.matchLicenseFilePath) return [];
    return [
      {
        id: makeUuid(),
        filePath: defaultValues.matchLicenseFilePath,
        name: defaultValues.matchLicenseFilePath.split("/").pop() || "",
        status: 1,
      } as unknown as TExtendFile,
    ];
  }, [defaultValues?.matchLicenseFilePath]);

  const [licenseFiles, setLicenseFiles] = useState<TExtendFile[]>(initialLicenseFile);
  const confirmOverwriteDialog = useDialog();

  const handleLicenseFileChange = useCallback(
    async (value: TExtendFile | TExtendFile[] | null) => {
      const files = value ? (Array.isArray(value) ? value : [value]) : [];
      if (files.length === 0) {
        setLicenseFiles([]);
        setValue("matchLicenseFile", null, { shouldDirty: true });
        setValue("matchLicenseFilePath", null, { shouldDirty: true });
        return;
      }

      const file = files[0];
      if (file.filePath) {
        // Existing file (already on server) — keep filePath
        setLicenseFiles([file]);
        return;
      }

      // New raw file — confirm overwrite if a file already exists
      if (licenseFiles.length > 0) {
        const confirmed = await confirmOverwriteDialog.show();
        if (!confirmed) return;
      }

      // Proceed with overwrite — store File object for backend upload on save
      const displayFile = Object.assign(file, { id: makeUuid(), status: 1 }) as TExtendFile;
      setLicenseFiles([displayFile]);
      setValue("matchLicenseFile", file as unknown as File, { shouldDirty: true });
      setValue("matchLicenseFilePath", null, { shouldDirty: true });
    },
    [setValue, licenseFiles, confirmOverwriteDialog],
  );

  const handleRemoveLicenseFile = useCallback(() => {
    setLicenseFiles([]);
    setValue("matchLicenseFile", null, { shouldDirty: true });
    setValue("matchLicenseFilePath", null, { shouldDirty: true });
  }, [setValue]);

  return (
    <InnerBox showDivider={false} padding={3} borderColor={divider.high}>
      <CollapsibleSectionHeader title="基本情報" expanded={expanded} onToggle={toggle} />

      <Collapse in={expanded}>
        <Grid mt={3} container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="matchName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="matchNameRomaji">
              <Input cleanWhitespace />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="matchLicenseNumber">
              <Input readonly />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="matchLicenseExpiryDate">
              <InputDatePicker />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="representativeName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="representativePosition">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={4}>
            <FormField control={control} name="phoneNumber">
              <Input />
            </FormField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="cap12" color={black[70]} mb={1}>
              許可証
            </Typography>
            <DropZone
              nonControl
              value={licenseFiles}
              onChange={handleLicenseFileChange}
              options={{ multiple: false }}
              noClick
              enableFileList={false}
              rules={{
                acceptMimeTypes: [...MIME_TYPES.PDF],
                acceptFileTypesExtension: [...EXTENSIONS.PDF],
                maxSize: 10 * 1000 * 1000,
              }}
              childrenRenderHtml={({ open }) => (
                <>
                  <Typography variant="body14" color={text.secondary}>
                    アップロードするファイルをドラッグ&ドロップ
                  </Typography>
                  <Typography variant="body14" color={text.tertiary}>
                    （ファイル形式：PDFに対応）
                  </Typography>
                  <Typography variant="body14" color={text.secondary}>
                    または
                  </Typography>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      open();
                    }}
                  >
                    ファイルを選択する
                  </Button>
                </>
              )}
            />
            {licenseFiles.length > 0 && (
              <Stack mt={1}>
                {licenseFiles.map((file) => {
                  const fileProps = file.filePath ? { filePath: file.filePath } : { file: file as unknown as File };
                  return (
                    <Stack key={file.id} direction="row" alignItems="center" width="100%">
                      <FileItem
                        {...fileProps}
                        downloadable={!!file.filePath}
                        previewEnabled={!!file.filePath}
                        iconDownloadable={!!file.filePath}
                        hideIcon
                        files={file.filePath ? [{ filePath: file.filePath }] : undefined}
                      />
                      <Box flex={1} />
                      <IconBox onClick={handleRemoveLicenseFile}>
                        <IconClose />
                      </IconBox>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Collapse>

      <ConfirmSaveModal
        title="上書きの確認"
        open={confirmOverwriteDialog.open}
        onOk={confirmOverwriteDialog.confirm}
        onClose={confirmOverwriteDialog.cancel}
      >
        <Typography component="p" variant="subtitle1">
          すでにアップロード済みですが上書きしてもよろしいですか？
        </Typography>
      </ConfirmSaveModal>
    </InnerBox>
  );
};
